import type { GridCellParams } from '@mui/x-data-grid-pro';
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import type { BlockContent } from 'components/editor/grid/reduxStore/editorSlice';
import type { ColumnTypeIdentifier, TableColumnType, TableRowType } from 'components/editor/grid/reduxStore/table.types';
import { TableTypeIdentifier } from 'components/editor/grid/reduxStore/table.types';
import { isGeneratedRow } from 'components/editor/grid/reduxStore/crmRowMarkers';
import { PreviewBlock } from 'components/editor/GridDndEditor/Block/Preview/PreviewBlock';
import { updateSectionWithBlockUpdate } from 'components/editor/GridDndEditor/Block/Preview/PreviewTableBlock';
import PreviewTableFooter from 'components/editor/GridDndEditor/Block/Preview/PreviewTableFooter';
import { useApplyCustomRulesToColumnsInPreview } from 'components/editor/GridDndEditor/Block/Preview/useApplyCustomRulesToColumnsInPreview';
import { DEFAULT_DECIMAL_PLACES } from 'components/editor/GridDndEditor/Block/Table/defaultData';
import { isNumericColumn } from 'components/editor/GridDndEditor/Block/Table/helper';
import { getEngineRowsWithCalculatedSubtotals } from 'components/editor/GridDndEditor/Block/Table/tableUtils/getEngineRowsWithCalculatedSubtotals';
import { getIsCellEditingAllowed } from 'components/editor/GridDndEditor/Block/Table/tableUtils/customCellHelpers';
import { useTableCellModes } from 'components/editor/GridDndEditor/Block/Table/useTableCellModes';
import { gridPixelSize } from 'components/editor/shared/gridConfig';
import { useCellBackgroundColorStyles } from 'components/editor/GridDndEditor/Block/Table/useCellBackgroundColorStyles';
import { getRowClassName } from 'muiTheme/dataGridUtils';
import { useCallback, useMemo, useState } from 'react';
import type { CurrencySettings, SectionByDocumentIdApiResponse } from 'services/documents/DocumentRepository';
import { useContentSectionsForPreview } from './useContentSectionsForPreview';

interface PreviewContentTableBlockProps {
  block: BlockContent;
  isDocumentLocked: boolean;
  isTemplate?: boolean;
  section: SectionByDocumentIdApiResponse;
  currencySettings?: CurrencySettings;
  rotation?: number;
}

export function PreviewContentTableBlock({
  block,
  isDocumentLocked,
  section,
  isTemplate,
  currencySettings,
  rotation,
}: PreviewContentTableBlockProps) {
  const apiRef = useGridApiRef();
  const { sections, updateSections } = useContentSectionsForPreview();
  const blockId = block.blockConfig.id;
  const sectionId = section.id;
  const columnsData = block?.contentTable?.columns;

  const tableColumns = useMemo(() => (columnsData || []) as TableColumnType[], [columnsData]);
  const contentTableRows = block?.contentTable?.rows || [];
  const filteredRows = useMemo(() => contentTableRows.filter((row) => !row.isRowHidden), [contentTableRows]);
  const visibleRows = useMemo(
    () => (isTemplate ? filteredRows.filter((row) => !isGeneratedRow(row)) : filteredRows),
    [filteredRows, isTemplate]
  );
  const [allTableRows, setAllTableRows] = useState<TableRowType[]>(visibleRows);
  const { handleCellClick, handleCellModesModelChange, cellModesModel } = useTableCellModes();

  const { applyCustomRulesToColumnsInPreview } = useApplyCustomRulesToColumnsInPreview({
    blockId: block.blockConfig.id,
    pricingDecimalPlaces: block.contentTable?.metadata.pricingDecimalPlaces || DEFAULT_DECIMAL_PLACES,
    isTemplate,
    currencySettings,
  });

  const handleIsCellEditable = (params: GridCellParams): boolean => {
    const { row, colDef } = params;
    if (isDocumentLocked) {
      return false;
    }

    const isCellEditingAllowed = getIsCellEditingAllowed({ row, colDef });

    return isCellEditingAllowed;
  };

  const handleIsCellNumeric = (params: GridCellParams): boolean => {
    const { colDef } = params;

    return isNumericColumn(colDef['columnType'] as ColumnTypeIdentifier);
  };

  const updateRowsOptionalStatus = useCallback(
    (updatedRow: TableRowType) => {
      const { rows } = apiRef.current.store.value;
      const { dataRowIdToModelLookup, dataRowIds } = rows;
      const updatedRows = dataRowIds.map((rowId: string) =>
        dataRowIdToModelLookup[rowId].id === updatedRow.id
          ? { ...dataRowIdToModelLookup[rowId], isRowOptional: updatedRow.isRowOptional, ...updatedRow }
          : dataRowIdToModelLookup[rowId]
      );
      setAllTableRows(updatedRows);

      if (section) {
        updateSectionWithBlockUpdate(section, blockId, updatedRows, updateSections, sections, sectionId);
      }
    },
    [apiRef, section, block, blockId, sections, sectionId, updateSections]
  );

  const gridColumns = useMemo(
    () => applyCustomRulesToColumnsInPreview(tableColumns, updateRowsOptionalStatus, isDocumentLocked),
    [tableColumns, applyCustomRulesToColumnsInPreview, updateRowsOptionalStatus]
  );
  const pricingDecimalPlaces = block?.contentTable?.metadata?.pricingDecimalPlaces ?? DEFAULT_DECIMAL_PLACES;

  const enginePricing = useMemo(
    () => getEngineRowsWithCalculatedSubtotals(allTableRows, tableColumns, currencySettings, pricingDecimalPlaces),
    [allTableRows, tableColumns, currencySettings, pricingDecimalPlaces]
  );

  const { textRowsWithCalculatedSubtotalsColumn, sumOfSubtotalColumn } = enginePricing;
  const { tableType = null } = block?.contentTable?.metadata ?? {};
  const totalRows = block?.contentTable?.totalRows as TableRowType[];
  const isPricingTable = tableType === TableTypeIdentifier.PRICING_TABLE;
  const { cellBackgroundColorClassName, cellBackgroundColorStyles } = useCellBackgroundColorStyles(textRowsWithCalculatedSubtotalsColumn);

  return (
    <PreviewBlock key={block.blockConfig.id} gridBlock={block} gridSystemInPx={gridPixelSize} rotation={rotation}>
      {cellBackgroundColorStyles}
      <DataGridPro
        apiRef={apiRef}
        rows={textRowsWithCalculatedSubtotalsColumn}
        columns={gridColumns}
        className={`py-selected-state py-preview-table-style ${cellBackgroundColorClassName}`}
        isCellEditable={(params) => handleIsCellEditable(params)}
        onCellClick={handleCellClick}
        getCellClassName={(params) => (handleIsCellNumeric(params) ? 'py-is-numeric-cell' : '')}
        cellModesModel={cellModesModel}
        onCellModesModelChange={handleCellModesModelChange}
        getRowClassName={(params) => getRowClassName(params.row.rowType)}
        sx={{
          width: block.blockConfig.width,
          ...(isPricingTable && {
            borderRadius: 'var(--unstable_DataGrid-radius) var(--unstable_DataGrid-radius) 0 0',
          }),
        }}
      />
      {isPricingTable && (
        <PreviewTableFooter
          currencySettings={currencySettings}
          isTemplate={isTemplate}
          updatedTotalRows={totalRows}
          sumOfSubtotalColumn={sumOfSubtotalColumn}
          pricingDecimalPlaces={block?.contentTable?.metadata?.pricingDecimalPlaces || DEFAULT_DECIMAL_PLACES}
        />
      )}
    </PreviewBlock>
  );
}
